import React, { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, X } from "lucide-react";

const PRIMARY = "#6C47FF";

function summaryLabel(selected, defaultLabel, kind) {
  if (!selected || selected.length === 0) return defaultLabel;
  if (selected.length === 1) return selected[0];
  if (selected.length === 2) return `${selected[0]}, ${selected[1]}`;
  return `${selected.length} ${kind} selected`;
}

export default function MultiSelectFilter({ label, kind, options, value = [], onChange, testid, renderOption }) {
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [draft, setDraft] = useState(value);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const upd = () => setIsMobile(mq.matches);
    upd();
    mq.addEventListener("change", upd);
    return () => mq.removeEventListener("change", upd);
  }, []);

  useEffect(() => {
    if (open) setDraft(value);
  }, [open, value]);

  useEffect(() => {
    if (!open || isMobile) return;
    const onDoc = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open, isMobile]);

  const toggle = (opt) => {
    const set = new Set(isMobile ? draft : value);
    if (set.has(opt)) set.delete(opt);
    else set.add(opt);
    const next = Array.from(set);
    if (isMobile) setDraft(next);
    else onChange(next);
  };

  const selectAll = () => {
    const all = options.map((o) => (typeof o === "string" ? o : o.value));
    if (isMobile) setDraft(all);
    else onChange(all);
  };
  const clear = () => {
    if (isMobile) setDraft([]);
    else onChange([]);
  };

  const applyMobile = () => { onChange(draft); setOpen(false); };
  const cancelMobile = () => setOpen(false);

  const activeList = isMobile && open ? draft : value;
  const btnLabel = summaryLabel(activeList, label, kind);

  const Panel = (
    <div className={isMobile ? "flex flex-col h-full" : ""}>
      <div className="flex items-center justify-between px-3 py-2 text-xs font-[600]">
        <button onClick={selectAll} data-testid={`${testid}-select-all`} className="text-[#6C47FF] hover:underline">Select All</button>
        <button onClick={clear} data-testid={`${testid}-clear`} className="text-[#7f1d1d] hover:underline">Clear</button>
      </div>
      <div className="border-t border-[#E8EAEF]" />
      <div className={`overflow-y-auto ${isMobile ? "flex-1" : "max-h-[280px]"}`}>
        {options.map((opt) => {
          const val = typeof opt === "string" ? opt : opt.value;
          const disp = typeof opt === "string" ? opt : opt.label;
          const checked = activeList.includes(val);
          return (
            <button
              key={val}
              onClick={() => toggle(val)}
              data-testid={`${testid}-option-${val}`}
              className="w-full flex items-center gap-2 px-3 text-left text-sm hover:bg-[#F0F1F7]"
              style={{ minHeight: isMobile ? 48 : 36 }}
            >
              <span
                className="flex-shrink-0 h-4 w-4 rounded-[3px] border flex items-center justify-center"
                style={{ borderColor: checked ? PRIMARY : "#c3c1bd", backgroundColor: checked ? PRIMARY : "#fff" }}
              >
                {checked && <Check className="h-3 w-3 text-white" />}
              </span>
              <span className="text-[#1A1A2E]">{renderOption ? renderOption(val, disp) : disp}</span>
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="relative inline-block" ref={wrapperRef} data-testid={testid}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testid}-trigger`}
        className="inline-flex items-center gap-2 rounded-[6px] border border-[#E8EAEF] px-2 py-2 min-h-[44px] text-sm font-[600] bg-white text-[#1A1A2E] hover:border-[#6C47FF]"
      >
        <span className="truncate max-w-[180px]">{btnLabel}</span>
        {value.length > 0 && (
          <span
            className="inline-flex items-center justify-center text-[10px] font-[700] rounded-full text-white"
            style={{ backgroundColor: PRIMARY, minWidth: 18, height: 18, padding: "0 5px" }}
            data-testid={`${testid}-count`}
          >
            {value.length}
          </span>
        )}
        <ChevronDown className="h-4 w-4 text-[#6B7280]" />
      </button>

      {open && !isMobile && (
        <div className="absolute z-40 left-0 mt-2 w-[240px] bg-white border border-[#E8EAEF] rounded-[8px] shadow-[0_8px_24px_rgba(0,0,0,0.12)]">
          {Panel}
        </div>
      )}

      {open && isMobile && (
        <>
          <div className="fixed inset-0 z-40" style={{ backgroundColor: "rgba(0,0,0,0.5)" }} onClick={cancelMobile} />
          <div className="fixed inset-x-0 bottom-0 z-50 bg-white rounded-t-[16px] flex flex-col" style={{ maxHeight: "80vh" }}>
            <div className="flex justify-center pt-2 pb-1">
              <div className="h-1.5 w-10 rounded-full bg-[#E8EAEF]" />
            </div>
            <div className="px-4 pb-1 flex items-center justify-between">
              <div className="text-sm font-[600]">{label}</div>
              <button onClick={cancelMobile} className="p-2"><X className="h-4 w-4" /></button>
            </div>
            {Panel}
            <div className="grid grid-cols-2 gap-3 p-4 border-t border-[#E8EAEF]">
              <button onClick={cancelMobile} className="rounded-[8px] border border-[#6C47FF] text-[#6C47FF] text-sm font-[600]" style={{ minHeight: 48 }}>Cancel</button>
              <button onClick={applyMobile} data-testid={`${testid}-apply`} className="rounded-[8px] btn-mx text-sm font-[600]" style={{ minHeight: 48 }}>Apply</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
